import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import { useEffect, useRef } from 'react';
import {
  DANGER_COLOR,
  LARGE_COLOR,
  quillDeltaToTaskMarkup,
  taskMarkupToEditorHtml,
} from '../utils/taskRichTextMarkup';

interface RichTaskTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  minRows?: number;
  placeholder?: string;
  disabled?: boolean;
}

const TOOLBAR_OPTIONS = [
  ['bold'],
  [{ color: [false, DANGER_COLOR] }],
  [{ size: [false, 'large'] }],
  ['clean'],
];

export function RichTaskTextEditor({
  value,
  onChange,
  minRows = 7,
  placeholder,
  disabled = false,
}: RichTaskTextEditorProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<Quill | null>(null);
  const lastEmittedRef = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const minHeight = Math.max(minRows, 3) * 24 + 16;

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount || quillRef.current) return;

    const editorHost = document.createElement('div');
    mount.appendChild(editorHost);

    const quill = new Quill(editorHost, {
      theme: 'snow',
      placeholder,
      modules: {
        toolbar: TOOLBAR_OPTIONS,
      },
    });
    quillRef.current = quill;
    quill.clipboard.dangerouslyPasteHTML(taskMarkupToEditorHtml(value), 'silent');
    lastEmittedRef.current = value;

    const editor = mount.querySelector<HTMLElement>('.ql-editor');
    if (editor) {
      editor.style.minHeight = `${minHeight}px`;
      editor.classList.add('text-sm', 'leading-6', 'text-gray-900');
    }

    quill.on('text-change', () => {
      const next = quillDeltaToTaskMarkup(quill.getContents());
      lastEmittedRef.current = next;
      onChangeRef.current(next);
    });

    return () => {
      quillRef.current = null;
      mount.innerHTML = '';
    };
  }, []);

  useEffect(() => {
    const quill = quillRef.current;
    if (!quill || value === lastEmittedRef.current) return;
    const selection = quill.getSelection();
    quill.clipboard.dangerouslyPasteHTML(taskMarkupToEditorHtml(value), 'silent');
    if (selection) {
      quill.setSelection(selection);
    }
    lastEmittedRef.current = value;
  }, [value]);

  useEffect(() => { quillRef.current?.enable(!disabled); }, [disabled]);

  return (
    <div className="rounded-md bg-white [&_.ql-toolbar.ql-snow]:rounded-t-md [&_.ql-container.ql-snow]:rounded-b-md [&_.ql-toolbar.ql-snow]:border-gray-300 [&_.ql-container.ql-snow]:border-gray-300 [&_.ql-picker-label]:text-gray-700 [&_.ql-stroke]:stroke-gray-700">
      <div ref={mountRef} />
      <div className="hidden" style={{ color: LARGE_COLOR }} />
    </div>
  );
}
